import React, { useEffect, useRef } from "react";

/**
 * Cursor sparkle particles — lightweight canvas emitter that drops
 * short-lived gold + blue sparks along the mouse path. Zero deps, 60fps,
 * respects prefers-reduced-motion. Rendered above the hero but pointer-events:none
 * so it never breaks clicks.
 */
export default function CursorSparkles({ colors = ["#f5c443", "#3b82f6", "#22d3ee"], max = 25 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // Skip on touch — sparkles need a real cursor
    if ("ontouchstart" in window && window.matchMedia("(pointer: coarse)").matches) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    let particles = [];
    let raf = 0;
    let lastX = 0, lastY = 0;

    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const spawn = (x, y, vx, vy) => {
      if (particles.length >= max) return;
      const color = colors[(Math.random() * colors.length) | 0];
      particles.push({
        x, y,
        vx: vx * 0.3 + (Math.random() - 0.5) * 1.2,
        vy: vy * 0.3 + (Math.random() - 0.5) * 1.2 - 0.4,
        life: 1,
        size: 1 + Math.random() * 2.4,
        color,
      });
    };

    const onMove = (e) => {
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      const speed = Math.hypot(dx, dy);
      // Only spawn on real movement — fewer, more purposeful sparkles.
      if (speed < 6) { lastX = e.clientX; lastY = e.clientY; return; }
      const count = Math.min(2, Math.max(1, Math.floor(speed / 20)));
      for (let i = 0; i < count; i++) spawn(e.clientX, e.clientY, dx, dy);
      lastX = e.clientX;
      lastY = e.clientY;
    };

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Additive blend for glowy overlap
      ctx.globalCompositeOperation = "lighter";
      particles = particles.filter(p => p.life > 0);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.015;   // gentle gravity
        p.vx *= 0.985;   // drag
        p.vy *= 0.985;
        p.life -= 0.02;
        const r = p.size * (0.5 + p.life * 0.5);
        // Glow
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 4);
        grad.addColorStop(0, p.color + "cc");
        grad.addColorStop(0.4, p.color + "55");
        grad.addColorStop(1, p.color + "00");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 4, 0, Math.PI * 2);
        ctx.fill();
        // Core
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("resize", resize);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", resize);
    };
  }, [colors, max]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[60]"
      data-testid="cursor-sparkles"
      aria-hidden="true"
    />
  );
}
